import { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Snowflake, MessageSquare, Calendar, X } from 'lucide-react';
import type { Operacao } from '../types';
import { SUB_STATUS_CONFIG_POR_ENUM, ESTEIRA_COLOR, ORIGEM_STYLE } from '../types';

interface KanbanCardProps {
  operacao: Operacao;
  index: number;
  onClick: (op: Operacao) => void;
}

function formatMoeda(valor: number | null | undefined): string {
  if (valor == null || valor === 0) return '-';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

// SUB_STATUS_CONFIG_POR_ENUM usado aqui apenas como cache de display no board
// principal. A fonte de verdade é a tabela etapas_sub_esteira (consumida pelo
// SubBoard via API). Chaveado pelo enum bruto (esteiraAtualEnum), não pelo
// bucket visual - o bucket "Crédito" (esteira=1) agrupa ENTRADA e CREDITO,
// cujas listas de sub-status são diferentes.
function faseLabel(op: Operacao): string {
  return SUB_STATUS_CONFIG_POR_ENUM[op.esteiraAtualEnum]?.[op.subStatus - 1]?.label ?? '-';
}

function localidade(op: Operacao): string | null {
  if (op.imovelDetalhe?.cidade) {
    return op.imovelDetalhe.estado
      ? `${op.imovelDetalhe.cidade} / ${op.imovelDetalhe.estado}`
      : op.imovelDetalhe.cidade;
  }
  if (op.enderecoImovel) {
    const match = op.enderecoImovel.match(/([A-Za-zÀ-ú ]+\/[A-Z]{2})\s*$/);
    if (match) return match[1].trim();
  }
  return null;
}

function iniciais(nome: string): string {
  return nome.split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase() || '?';
}

function formatPrazo(prazo?: string): string {
  if (!prazo) return 'sem prazo';
  try { return new Date(prazo).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); } catch { return 'sem prazo'; }
}

export default function KanbanCard({ operacao: op, index, onClick }: KanbanCardProps) {
  const [tarefasAbertas, setTarefasAbertas] = useState(false);

  const congelada = op.status === 'CONGELADA';
  const origem = ORIGEM_STYLE[op.origem] ?? { badgeBg: '#F1F3F5', badgeTxt: '#5F6470', stripe: '#9AA0AC' };
  const esteiraColor = ESTEIRA_COLOR[op.esteira];
  const fase = faseLabel(op);
  const local = localidade(op);
  // Item 4: valor de FINANCIAMENTO (fallback p/ o desejado quando o efetivo ainda não veio)
  const valor = formatMoeda(op.valorFinanciamento ?? op.valorFinanciamentoDesejado);
  const nomeComprador = op.compradores[0]?.nome ?? '-';
  // Item 4: "Origem - Cliente (CPF)" — CPF só dígitos
  const cpf = op.compradores[0]?.cpf ? op.compradores[0]!.cpf!.replace(/\D/g, '') : null;
  const tituloCliente = `${op.origem} - ${nomeComprador}${cpf ? ` (${cpf})` : ''}`;
  const coPartes = op.compradores.length - 1 + op.vendedores.length;
  const temResponsavel = !!op.responsavelNome && op.responsavelNome !== 'Sem responsável';
  const tarefas = op.tarefas ?? [];

  const aging =
    op.diasNaEsteira > 10 ? 'bg-red-50 text-red-600' :
    op.diasNaEsteira > 5  ? 'bg-amber-50 text-amber-700' :
                             'bg-[#EAECF2] text-[#5F6470]';

  // Pré-CCV (SPEC 3): estado derivado, sem migration - ccvDataAssinatura null = pré-CCV.
  const preCcv = !op.ccvDataAssinatura && op.origem === '5A';

  return (
    <Draggable draggableId={op.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...(provided.draggableProps.style as React.CSSProperties),
            opacity: congelada ? 0.6 : 1,
          }}
          {...provided.dragHandleProps}
          onClick={() => onClick(op)}
          className={`
            relative bg-white rounded-[8px] border border-[#E2E6ED] overflow-visible
            cursor-pointer select-none transition-all duration-150
            hover:border-[#C5CDD8] hover:bg-[#FAFBFC]
            ${snapshot.isDragging ? 'shadow-lg rotate-[0.5deg]' : 'shadow-sm'}
          `}
        >
          {/* Faixa 3px por origem */}
          <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[8px]" style={{ backgroundColor: origem.stripe }} />

          <div className="pl-3 pr-3 pt-2.5 pb-2.5">
            {/* Linha 1: número + farol de eventos + badge origem */}
            <div className="flex items-center justify-between gap-1.5 mb-1.5">
              <span className="text-[10px] text-[#9AA0AC] font-mono tabular-nums truncate">{op.numeroProcesso}</span>
              <div className="flex items-center gap-1 shrink-0">
                {!!op.eventosNaoVistos && op.eventosNaoVistos > 0 && (
                  <span
                    className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-[2px] rounded-full tabular-nums ${op.temAcionavelNaoVisto ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}
                    title={`${op.eventosNaoVistos} evento(s) novo(s) desde a última vez que esta operação foi aberta`}
                  >
                    <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ backgroundColor: op.temAcionavelNaoVisto ? '#B45309' : '#1D4ED8' }} />
                    {op.eventosNaoVistos}
                  </span>
                )}
                <span className="text-[10px] font-medium px-1.5 py-[2px] rounded" style={{ backgroundColor: origem.badgeBg, color: origem.badgeTxt }}>{op.origem}</span>
              </div>
            </div>

            {/* Linha 2: "Origem - Cliente (CPF)" + ícone de calendário (tarefas pendentes) */}
            <div className="flex items-start justify-between gap-1.5">
              <p className="text-[13px] font-medium text-[#1A1D23] leading-snug truncate" title={tituloCliente}>
                {tituloCliente}
                {coPartes > 0 && <span className="text-[11px] font-normal text-[#9AA0AC] ml-1">+{coPartes}</span>}
              </p>
              {op.tarefasPendentes > 0 && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTarefasAbertas((v) => !v); }}
                    title={`${op.tarefasPendentes} tarefa(s) pendente(s)`}
                    className="flex items-center gap-0.5 text-[10px] font-medium text-[#5F6470] bg-[#EAECF2] hover:bg-[#DCE0E8] px-1.5 py-[2px] rounded transition-colors"
                  >
                    <Calendar size={11} /> {op.tarefasPendentes}
                  </button>
                  {tarefasAbertas && (
                    <div className="absolute right-0 top-[115%] z-30 w-60 bg-white rounded-lg border border-[#E2E6ED] shadow-xl p-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold text-[#1A1D23]">Tarefas pendentes</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setTarefasAbertas(false); }} className="text-[#9AA0AC] hover:text-[#5F6470]"><X size={13} /></button>
                      </div>
                      {tarefas.length === 0 ? (
                        <p className="text-[11px] text-[#9AA0AC]">Sem detalhes das tarefas.</p>
                      ) : (
                        <ul className="space-y-1 max-h-48 overflow-y-auto">
                          {tarefas.map((t) => (
                            <li key={t.id} className="flex items-center justify-between gap-2 text-[11px] border border-[#EEF0F4] rounded px-2 py-1">
                              <span className="text-[#3A3F4A] truncate" title={t.titulo}>{t.titulo}</span>
                              <span className="text-[#9AA0AC] shrink-0 tabular-nums">{formatPrazo(t.prazo)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Linha 3: valor de financiamento (logo abaixo, pouco separado) */}
            <p className="text-[12px] font-semibold text-[#1A1D23] tabular-nums mt-0.5">{valor}</p>

            {/* Linha 4: cidade/UF + fase (contexto) */}
            {(local || fase) && (
              <p className="text-[11px] text-[#5F6470] mt-0.5 truncate">
                {[local, fase].filter((x) => x && x !== '-').join(' · ')}
              </p>
            )}

            {/* Divisor */}
            <div className="my-2 border-t border-[#EAECF2]" />

            {/* Rodapé: foto/avatar do analista (esq) × tempo na etapa (dir) */}
            <div className="flex items-center justify-between gap-1.5">
              <div className="flex items-center gap-1.5 min-w-0" title={temResponsavel ? op.responsavelNome : 'Sem responsável'}>
                {temResponsavel && op.responsavelFoto ? (
                  <img src={op.responsavelFoto} alt={op.responsavelNome} className="shrink-0 w-6 h-6 rounded-full object-cover" />
                ) : temResponsavel ? (
                  <span className="shrink-0 w-6 h-6 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center text-[10px] font-semibold">
                    {iniciais(op.responsavelNome)}
                  </span>
                ) : (
                  <span className="shrink-0 w-6 h-6 rounded-full bg-[#EAECF2] text-[#9AA0AC] flex items-center justify-center text-[10px] font-semibold">—</span>
                )}
                <span className="text-[11px] text-[#5F6470] truncate">{temResponsavel ? op.responsavelNome : 'Sem responsável'}</span>
              </div>
              <span className={`shrink-0 text-[10px] px-1.5 py-[2px] rounded tabular-nums font-medium ${aging}`} title="Tempo na etapa atual">{op.diasNaEsteira}d</span>
            </div>

            {/* Badges extras - estado (compactos) */}
            {(op.mensagensNaoLidas > 0 || congelada || preCcv || (op.clienteConduz && op.esteira === 3)) && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {preCcv && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-[1px] rounded" title="Importado antes da assinatura do CCV (exceção manual)">pré-CCV</span>}
                {congelada && <span className="inline-flex items-center gap-0.5 text-[9px] bg-[#EAECF2] text-[#5F6470] px-1.5 py-[1px] rounded"><Snowflake size={9} /> pausada</span>}
                {op.clienteConduz && op.esteira === 3 && <span className="text-[9px] bg-teal-50 text-teal-700 px-1.5 py-[1px] rounded">CC</span>}
                {op.mensagensNaoLidas > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-[1px] rounded"><MessageSquare size={9} /> {op.mensagensNaoLidas}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}
